import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Card,
  LinearProgress,
  Alert,
  AlertTitle,
  Chip,
  List,
  ListItem,
  ListItemText,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { useForm, Controller } from 'react-hook-form';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusCard from '../../components/common/StatusCard';
import ErrorMessage from '../../components/common/ErrorMessage';
import { useCrawlContext } from '../../context/CrawlContext';
import CrawlJobList from '../../components/common/CrawlJobList';

const CrawlPage = () => {
  const [activeTab, setActiveTab] = useState('form');
  const {
    jobs: crawlJobs,
    loading: isJobsLoading,
    error,
    addJob,
    deleteJob,
    refreshJob,
    fetchJobs,
    restartJob,
    markAsCompleted,
  } = useCrawlContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isMarkingCompleted, setIsMarkingCompleted] = useState(false);
  
  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      url: '',
      depth: 1,
      max_pages: 3000,
      follow_external_links: false,
      restrict_to_base_path: false,
      extract_embedded_files: true,
      file_types_to_extract: ['pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'hwp', 'hwpx', 'jpg', 'png'],  // ✅ 이미지 포함 (한글 OCR 개선)
      save_html_content: true,
      save_webpage_text: true,
      ignore_robots_txt: false,  // ✅ robots.txt 무시 옵션 추가
    }
  });

  const fileTypeOptions = [
    { value: 'pdf', label: 'PDF (.pdf)' },
    { value: 'docx', label: 'Word (.docx)' },
    { value: 'doc', label: 'Word (.doc)' },
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'xls', label: 'Excel (.xls)' },
    { value: 'pptx', label: 'PowerPoint (.pptx)' },
    { value: 'ppt', label: 'PowerPoint (.ppt)' },
    { value: 'hwp', label: 'HWP (.hwp)' },
    { value: 'hwpx', label: 'HWPX (.hwpx)' },
    { value: 'jpg', label: '이미지 (.jpg, .jpeg)' },
    { value: 'png', label: '이미지 (.png)' },
  ];

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await addJob(data);
      reset();
      setActiveTab('jobs');
    } catch (error) {
      // 에러 처리는 useCrawlContext에서 제공
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelCrawl = async (crawlId) => {
    try {
      setIsCancelling(crawlId);
      await deleteJob(crawlId);
    } catch (error) {
      // 에러 처리는 useCrawlContext에서 제공
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRestartCrawl = async (crawlId) => {
    try {
      setIsRestarting(crawlId);
      await restartJob(crawlId);
      // 전체 목록 새로고침
      fetchJobs();
    } catch (error) {
      // 에러 처리는 useCrawlContext에서 제공
    } finally {
      setIsRestarting(false);
    }
  };

  const handleMarkCompleted = async (crawlId) => {
    try {
      setIsMarkingCompleted(crawlId);
      await markAsCompleted(crawlId);
      // 전체 목록 새로고침
      fetchJobs();
    } catch (error) {
      // 에러 처리는 useCrawlContext에서 제공
    } finally {
      setIsMarkingCompleted(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">크롤링 관리</Typography>
        <Box>
          <Button
            variant={activeTab === 'form' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('form')}
            startIcon={<AddIcon />}
            sx={{ mr: 1 }}
          >
            새 크롤링
          </Button>
          <Button
            variant={activeTab === 'jobs' ? 'contained' : 'outlined'}
            onClick={() => setActiveTab('jobs')}
            startIcon={<RefreshIcon />}
          >
            작업 목록
          </Button>
        </Box>
      </Box>

      {activeTab === 'form' ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            새 크롤링 작업 생성
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="url"
                  control={control}
                  rules={{ required: '크롤링할 URL을 입력해주세요.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="웹사이트 URL"
                      placeholder="https://example.com"
                      fullWidth
                      variant="outlined"
                      error={!!errors.url}
                      helperText={errors.url?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="depth"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>크롤링 깊이</InputLabel>
                      <Select {...field} label="크롤링 깊이">
                        <MenuItem value={1}>단일 페이지만</MenuItem>
                        <MenuItem value={2}>링크된 페이지까지 (1단계)</MenuItem>
                        <MenuItem value={3}>깊은 크롤링 (2단계)</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="max_pages"
                  control={control}
                  rules={{ required: '최대 페이지 수를 입력해주세요.', min: 1, max: 10000 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="최대 페이지 수"
                      type="number"
                      fullWidth
                      variant="outlined"
                      InputProps={{ inputProps: { min: 1, max: 10000 } }}
                      error={!!errors.max_pages}
                      helperText={errors.max_pages?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="follow_external_links"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                        />
                      }
                      label="외부 링크 크롤링"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="restrict_to_base_path"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                        />
                      }
                      label="기본 경로 제한 (하위 경로만 크롤링)"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="ignore_robots_txt"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                        />
                      }
                      label="🚨 robots.txt 무시 (고객 의뢰 크롤링용)"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="extract_embedded_files"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                        />
                      }
                      label="첨부 파일 추출"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  추출할 파일 유형
                </Typography>
                <Controller
                  name="file_types_to_extract"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {fileTypeOptions.map((option) => (
                        <Chip
                          key={option.value}
                          label={option.label}
                          clickable
                          color={value.includes(option.value) ? 'primary' : 'default'}
                          onClick={() => {
                            if (value.includes(option.value)) {
                              onChange(value.filter(v => v !== option.value));
                            } else {
                              onChange([...value, option.value]);
                            }
                          }}
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isLoading}
                  startIcon={isLoading && <LoadingSpinner size={20} />}
                >
                  크롤링 시작
                </Button>
              </Grid>
            </Grid>
          </form>
          
          {error && (
            <Box sx={{ mt: 2 }}>
              <ErrorMessage message={error} />
            </Box>
          )}
        </Paper>
      ) : (
        <>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between' }}>
            크롤링 작업 목록
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchJobs}
              variant="outlined"
              size="small"
            >
              새로고침
            </Button>
          </Typography>

          {isJobsLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage message="크롤링 작업 목록을 불러오는 중 오류가 발생했습니다." />
          ) : (
            <CrawlJobList
              jobs={crawlJobs}
              onDelete={handleCancelCrawl}
              onRefresh={refreshJob}
              onRestart={handleRestartCrawl}
              onMarkCompleted={handleMarkCompleted}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CrawlPage; 